"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Gauge, Target, ShieldCheck, FileCheck2, ArrowRight, Sparkles, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const challenges = [{
  id: "sample-throughput",
  icon: Gauge,
  title: "Sample Throughput & Bottlenecks",
  tagline: "High Testing Volumes vs. Release Times",
  description: "Traditional wet chemistry and offline testing slow batch release schedules. Labs face backlogs that directly delay manufacturing supply chains.",
  impact: "Impact: Hours of idle production line wait time per batch.",
  solutionLink: "/ProcessAnalyticalTechnologyPage",
  solutionLabel: "Explore PAT solutions"
}, {
  id: "analytical-accuracy",
  icon: Target,
  title: "Analytical Accuracy & Repeatability",
  tagline: "Minimizing Operator & Ambient Drift",
  description: "Variability in sample preparation and manual analysis leads to out-of-specification (OOS) investigations, false rejects, and wasted materials.",
  impact: "Impact: Costly investigations and batch quarantine risks.",
  solutionLink: "/PowderTabletTestingInstrumentsPage",
  solutionLabel: "View testing instruments"
}, {
  id: "regulatory-compliance",
  icon: ShieldCheck,
  title: "Strict Regulatory Compliance",
  tagline: "21 CFR Part 11 & Data Integrity",
  description: "Regulators require full audit trails, strict user access controls, and transparent data capture from raw material receipt through to final dosage.",
  impact: "Impact: Audit vulnerability and documentation overhead.",
  solutionLink: "/QualificationLifecycleServicesPage",
  solutionLabel: "See qualification services"
}, {
  id: "method-validation",
  icon: FileCheck2,
  title: "Method Validation Complexity",
  tagline: "Transferring & Scaling Analytical Methods",
  description: "Validating new spectroscopic or physical testing methods demands extensive chemometric calibration, ICH Q2 compliance, and technical expertise.",
  impact: "Impact: Extended validation timelines before deployment.",
  solutionLink: "/ChemometricModelDevelopmentPage",
  solutionLabel: "Review method development"
}];
export default function AnalysisChallenges() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="analysischallenges" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold tracking-wide text-xs uppercase">
            <Sparkles className="size-3.5 text-primary mr-1" />
            Operational Obstacles
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Addressing Core Laboratory Analysis Challenges
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Regulated laboratories and production teams across Ireland and the UK navigate complex hurdles daily. We provide direct technology and qualification pathways to resolve each friction point.
          </p>
        </div>

        {/* 4 Core Challenges Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {challenges.map((challenge, index) => {
          const IconComponent = challenge.icon;
          return <motion.div key={challenge.id} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border/80 hover:border-primary/50 shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 bg-card">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="p-3 rounded-lg bg-primary/10 text-primary w-fit flex items-center justify-center">
                          <IconComponent className="size-6 text-primary" />
                        </div>
                        <span className="text-xs font-mono font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-sm">
                          0{index + 1}
                        </span>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-bold font-default text-foreground mt-4">
                        {challenge.title}
                      </CardTitle>
                      <CardDescription className="text-sm font-medium text-primary">
                        {challenge.tagline}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-1">
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {challenge.description}
                      </p>
                      <div className="rounded-md bg-secondary/50 border border-border/60 p-3 text-xs sm:text-sm font-medium text-secondary-foreground">
                        {challenge.impact}
                      </div>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <Button asChild variant="ghost" className="p-0 h-auto font-semibold text-primary hover:text-primary/80 hover:bg-transparent inline-flex items-center gap-1.5">
                      <Link to={challenge.solutionLink}>
                        <span>{challenge.solutionLabel}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Context Banner */}
        <div className="mt-12 sm:mt-16 rounded-2xl bg-secondary/70 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="flex items-center gap-4 max-w-xl">
            <div className="hidden sm:flex size-12 shrink-0 rounded-full bg-primary/10 text-primary items-center justify-center">
              <HelpCircle className="size-6 text-primary" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-default font-semibold text-foreground">
                Need a tailored assessment for your laboratory method?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Our application team reviews existing methods and sample matrices to identify viable speed and accuracy gains.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
              <Link to="/AssessMyMethodPage">
                <span>Assess My Method</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                <span>Contact Specialists</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}